import { useState } from 'react';
import type { FormEvent } from 'react';
import type { ContactFormRequest, ContactFormResponse } from '@bpd/shared';

const inputCls =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-phoenix-blue-500 focus:outline-none focus:ring-1 focus:ring-phoenix-blue-500';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error' | 'not-configured';

const CONTACT_API_URL = import.meta.env.VITE_CONTACT_API_URL;

export function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot — hidden from real visitors via CSS; bots that fill every
  // field blindly trip it. Never labeled, never focusable by keyboard/tab.
  const [website, setWebsite] = useState('');
  const [state, setState] = useState<SubmitState>('idle');

  const resetForm = () => {
    setName('');
    setEmail('');
    setMessage('');
    setWebsite('');
    setState('idle');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // The contact-form backend doesn't exist yet in Phase 1 — degrade
    // gracefully rather than throwing a confusing network error at the visitor.
    if (!CONTACT_API_URL) {
      setState('not-configured');
      return;
    }

    setState('submitting');
    try {
      const body: ContactFormRequest & { website: string } = { name, email, message, website };
      const res = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = (await res.json()) as ContactFormResponse;
      setState(data.success ? 'success' : 'error');
    } catch {
      setState('error');
    }
  };

  return (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <h1 className="font-serif text-4xl font-bold text-phoenix-blue-900">Get in Touch</h1>
      <p className="mt-4 text-lg text-gray-700">
        We&apos;d love to hear about your project. Send us a message below and we&apos;ll be in
        touch soon.
      </p>

      {state === 'success' ? (
        <div className="mt-10 rounded-lg border border-gray-200 p-8 shadow-sm">
          <p className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            Thanks — your message has been sent. We&apos;ll be in touch soon.
          </p>
          <button
            type="button"
            onClick={resetForm}
            className="mt-5 text-sm font-medium text-phoenix-blue-600 underline hover:text-phoenix-blue-700"
          >
            Send another message
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="mt-10 space-y-5 rounded-lg border border-gray-200 p-8 shadow-sm"
        >
          {/* Honeypot — invisible to real visitors, catches bots that fill every
              field blindly. Not a real field: no label, off-screen, unfocusable. */}
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Message</label>
            <textarea
              required
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={inputCls}
            />
          </div>

          {state === 'not-configured' && (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Our online contact form isn&apos;t connected yet — please email us directly at{' '}
              <a href="mailto:info@bluephoenixdesigns.com" className="font-semibold underline">
                info@bluephoenixdesigns.com
              </a>{' '}
              in the meantime.
            </p>
          )}
          {state === 'error' && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              Something went wrong sending your message. Please try again in a moment.
            </p>
          )}

          <button
            type="submit"
            disabled={state === 'submitting'}
            className="w-full rounded-md bg-phoenix-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-phoenix-blue-700 disabled:opacity-50"
          >
            {state === 'submitting' ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      )}
    </main>
  );
}
